import React, { useContext, useRef, useState } from 'react';
import {
  AvDateRangePicker,
  AvDialog,
  FeatureFlags,
  FilterStyledPopper,
  getDateFromFilter,
  getStringConditionFromDatePicker,
  SelectTrigger,
  useClickOutside,
  ValueWithPreset,
} from '@fabric-ui/components';
import { TimeBucket } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { Box, Button, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import getNewRequestParams from '@utils/getNewRequestParams';
import { patchHistoricalQueryTimeRange } from '@views/CustomDashboards/hooks';
import { useAvContext } from '../../../../context/AvContextProvider';
import { FieldType as MappingFieldType } from '../../../Sources/Mapping/types';
import { Orientation } from '../../constants';
import { CustomDashboardContext } from '../../CustomDashboardContext';
import { hasTimeBucket } from '../../EditCustomDashboard/TimeBucketBreakdown/utils';
import { AllTypeWidgets, HistoricalFilterMode, TileSubType, WidgetCategory } from '../../types';
import { FieldType } from '../../types/types';
import { newDefaultTimeRange, TimeframeOptions } from './constants';
import { formatTimeRange } from './utils';

const TimeFilter = ({
  orientation = Orientation.Horizontal,
  isMultiBreakdown,
}: {
  orientation?: Orientation;
  isMultiBreakdown?: boolean;
}) => {
  const theme = useTheme();
  const { newWidget, setNewWidget } = useContext(CustomDashboardContext);
  const {
    measurements,
    featureFlags,
    accountEntities: { aggProjs },
  } = useAvContext();
  const projectionName = newWidget.requests[0].projectionId.name;
  const [timeframe, setTimeframe] = useState(
    newWidget.requests[0].timeRange ? TimeframeOptions.historic.value : TimeframeOptions.current.value
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [switchTimeFrameDialogIsOpen, setSwitchTimeFrameDialogIsOpen] = useState(false);

  const dateRangeInputRef = useRef(null);
  const datePickerRef = useRef(null);

  const dateObject = getDateFromFilter(newWidget.requests[0].timeRange ?? {});

  const triggerProps = {
    ref: dateRangeInputRef,
    onClick: () => setIsDatePickerOpen(true),
    open: isDatePickerOpen,
    chipCount: undefined,
    label: formatTimeRange(newWidget.requests[0].timeRange ?? {}),
    size: 'xSmall',
    sx: {
      width: '100%',
      border: `1px solid ${theme.palette.colors.neutrals[400]}`,
      background: 'transparent',
      color: theme.palette.colors.neutrals[800],
      fontWeight: 400,
    },
  } as const;

  const dialogOptions = {
    [TimeframeOptions.current.value]: {
      title: 'Switch to historical view?',
      text: 'You are about to switch this chart to historical analytics, this will enable date breakdown and may remove some of the existing measurements or dimensions currently selected.',
      onClick: initTimeRangeAndTimeBucketToWidget,
    },
    [TimeframeOptions.historic.value]: {
      title: 'Switch to current view?',
      text: 'You are about to switch this chart to current analytics, this will remove date breakdown and may remove some of the existing measurements currently selected.',
      onClick: resetTimeRangeAndTimeBucket,
    },
  };

  const handleClose = () => {
    setIsDatePickerOpen(false);
  };

  useClickOutside(datePickerRef, handleClose);

  const onApply = ({ value, preset }: ValueWithPreset) => {
    const timeRange = getStringConditionFromDatePicker({ value, preset }).dateCondition;

    setNewWidget({
      ...newWidget,
      requests: [
        {
          ...newWidget.requests[0],
          timeRange: patchHistoricalQueryTimeRange(timeRange),
        },
      ],
    } as AllTypeWidgets);
  };

  const onToggleButtonChange = () => {
    if (newWidget.requests[0].select.metrics.length || newWidget.requests[0].select.dims.length) {
      setSwitchTimeFrameDialogIsOpen(true);
    } else {
      dialogOptions[timeframe].onClick();
    }
  };

  function initTimeRangeAndTimeBucketToWidget() {
    const { newSelect, newGroupBy, newSortBy, newDrillDownHierarchy, newTop } = getNewRequestParams({
      fields: isMultiBreakdown ? [] : [TimeBucket.BUCKET_WEEK],
      fieldCategory: FieldType.Dimensions,
      widget: newWidget,
      isHistoricalAnalyticsFeatureFlag: featureFlags[FeatureFlags.HistoricalAnalytics],
    });

    const historicSelectedMetrics = newSelect.metrics.filter(
      selectedMetric => measurements.visible.find(metric => selectedMetric.name === metric.systemName)?.timeExpression
    );

    const nonDateSelectedDimensions = newSelect.dims.filter(
      selectedDim =>
        ![MappingFieldType.DateTime, MappingFieldType.Date].includes(
          aggProjs[projectionName]?.fieldList.INTERACTIVE.find(dim => dim.id === selectedDim.name)?.type as string
        )
    );

    const timeRange = getStringConditionFromDatePicker(newDefaultTimeRange).dateCondition;

    setNewWidget({
      ...newWidget,
      historicalFilterMode: HistoricalFilterMode.CURRENT,
      requests: [
        {
          ...newWidget.requests[0],
          select: { ...newSelect, metrics: historicSelectedMetrics, dims: nonDateSelectedDimensions },
          groupBy: newGroupBy,
          sorting: newSortBy,
          top: newTop,
          timeRange,
        },
      ],
      drillDownHierarchy: newDrillDownHierarchy,
      definition: {
        ...newWidget.definition,
        ...(newWidget.category === WidgetCategory.Table && {
          custom: {
            ...newWidget.definition.custom,
            crossTableView: newWidget.definition.custom.crossTableView ? newSelect.dims.length === 1 : false,
          },
        }),
      },
    } as AllTypeWidgets);

    setTimeframe(TimeframeOptions.historic.value);

    setSwitchTimeFrameDialogIsOpen(false);
  }

  function resetTimeRangeAndTimeBucket() {
    const { newSelect, newGroupBy, newSortBy, newDistinct, newTop } = getNewRequestParams({
      fields: newWidget.requests[0].select.dims.filter(({ name }) => hasTimeBucket(name)).map(({ name }) => name),
      fieldCategory: FieldType.Dimensions,
      widget: newWidget,
      isHistoricalAnalyticsFeatureFlag: featureFlags[FeatureFlags.HistoricalAnalytics],
    });

    const currentFilteredMetrics = newSelect.metrics.filter(
      selectedMetric => measurements.visible.find(metric => selectedMetric.name === metric.systemName)?.expression
    );
    const currentFilteredDims = newSelect.dims.filter(({ name }) => !hasTimeBucket(name));

    const currentFilteredSortBy = newSortBy.filter(sort => currentFilteredMetrics.find(metric => sort.name === metric.name));

    const { timeRange, ...rest } = newWidget.requests[0];

    setNewWidget({
      ...newWidget,
      historicalFilterMode: undefined,
      requests: [
        {
          ...rest,
          select: { ...newSelect, metrics: currentFilteredMetrics, dims: currentFilteredDims },
          groupBy: newGroupBy,
          sorting: currentFilteredSortBy,
          distinct: newDistinct,
          top: newTop,
        },
      ],
    } as AllTypeWidgets);

    setTimeframe(TimeframeOptions.current.value);

    setSwitchTimeFrameDialogIsOpen(false);
  }

  const dialogFooter = (
    <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
      <Button onClick={() => setSwitchTimeFrameDialogIsOpen(false)}>Cancel</Button>
      <Button variant="contained" onClick={dialogOptions[timeframe].onClick}>
        Continue
      </Button>
    </Box>
  );

  return (
    <Box sx={{ ...flex.col, gap: orientation === Orientation.Horizontal ? 2 : 1.5 }}>
      <AvDialog
        titleVariant="warning"
        title={dialogOptions[timeframe].title}
        open={switchTimeFrameDialogIsOpen}
        onClose={() => setSwitchTimeFrameDialogIsOpen(false)}
        footer={dialogFooter}>
        <div>{dialogOptions[timeframe].text}</div>
      </AvDialog>

      <Box
        sx={{
          ...(orientation === Orientation.Vertical ? flex.colItemsStart : flex.justifyBetweenCenter),
          gap: orientation === Orientation.Vertical ? 0.5 : 0,
        }}>
        <Box sx={{ fontSize: 12, color: theme.palette.colors.neutrals[550] }}>Timeframe</Box>
        <ToggleButtonGroup color="white" size="xSmall" exclusive value={timeframe} onChange={onToggleButtonChange}>
          <ToggleButton value={TimeframeOptions.current.value} disabled={newWidget.type === TileSubType.Trend}>
            {TimeframeOptions.current.icon}
            {TimeframeOptions.current.value}
          </ToggleButton>
          <ToggleButton value={TimeframeOptions.historic.value}>
            {TimeframeOptions.historic.icon}
            {TimeframeOptions.historic.value}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {timeframe === TimeframeOptions.historic.value && (
        <div>
          <Box sx={{ fontSize: 12, color: theme.palette.colors.neutrals[550], mb: orientation === 'horizontal' ? '2px' : 0.5 }}>
            Date range
          </Box>
          <SelectTrigger {...triggerProps} />
          <FilterStyledPopper
            open={isDatePickerOpen}
            anchorEl={dateRangeInputRef.current}
            placement="bottom-start"
            sx={{
              minWidth: '400px',
              width: 'fit-content',
              padding: 0,
            }}>
            <Box ref={datePickerRef}>
              <AvDateRangePicker
                value={dateObject.value}
                activePresetValue={dateObject.preset}
                onChange={onApply}
                onClose={handleClose}
                showAsCalendar
                isRange
                showRelativeOptions
                allowFutureDates={false}
                isFilterExpression
                showOptions={false}
                isEditableInput={false}
              />
            </Box>
          </FilterStyledPopper>
        </div>
      )}
    </Box>
  );
};

export default TimeFilter;
